import { RefObject, useState } from "react";
import { FlagIcon } from "@phosphor-icons/react";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { FlagDeficiencyDialog } from "./FlagDeficiencyDialog";

type Props = {
  caseBriefId?: number | null;
  projectId?: number | null;
  fieldLabel: string;
  value: string;
  fieldRef?: RefObject<HTMLInputElement | HTMLTextAreaElement>;
  className?: string;
};

// The setup fields are plain inputs, so there is no editor selection to hang a
// floating toolbar on: the flagged quote is the field's selected range, or all of it.
function quoteFromField(value: string, field: HTMLInputElement | HTMLTextAreaElement | null): string {
  const start = field?.selectionStart ?? null;
  const end = field?.selectionEnd ?? null;
  if (start === null || end === null || start === end) return value.trim();
  return value.slice(start, end).trim() || value.trim();
}

export function ProjectMetaFlagButton({
  caseBriefId,
  projectId,
  fieldLabel,
  value,
  fieldRef,
  className
}: Props) {
  const isFlagEnabled = useFeatureFlag("case-brief-flag");
  const [quote, setQuote] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!isFlagEnabled || !caseBriefId || !value.trim()) return null;

  return (
    <>
      <button
        type="button"
        data-testid={`project-meta-flag-${fieldLabel.toLowerCase().replace(/\s+/g, "-")}-button`}
        title={`Flag this ${fieldLabel.toLowerCase()}`}
        aria-label={`Flag this ${fieldLabel.toLowerCase()}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          setQuote(quoteFromField(value, fieldRef?.current ?? null));
          setIsDialogOpen(true);
        }}
        className={cn(
          "flex shrink-0 items-center justify-center h-7 w-7 rounded-md text-innoveraZinc-400",
          "opacity-0 transition-opacity hover:bg-innoveraZinc-50 hover:text-innoveraZinc-700",
          "group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100",
          className
        )}
      >
        <FlagIcon className="h-4 w-4" />
      </button>

      <FlagDeficiencyDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        target={{ kind: "caseBrief", caseBriefId, projectId }}
        quote={quote}
        contextLabel={`Project Setup · ${fieldLabel}`}
      />
    </>
  );
}
